import React, { useMemo } from 'react';
import { calculateShipments } from '@/utils/automated-shipping-utils';
import { getCurrencyLogo } from '@/utils/global';
import { IAutomatedPayment } from '@Saral-Influencer-Marketing/saral-utils/src/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

interface Props {
  frequency: number;
  endDate: string | null;
  currentDate: string;
  mode: 'create' | 'edit';
  automatedPayment?: IAutomatedPayment;
  amount: number;
  isRetry?: boolean;
}

const PaymentInfoTag: React.FC<Props> = ({
  frequency,
  endDate,
  currentDate,
  mode,
  automatedPayment,
  amount,
  isRetry,
}) => {
  const data = calculateShipments(currentDate, frequency, endDate);

  const { userDetails } = useSelector((state: any) => state.user);

  const currencyLogo = getCurrencyLogo(userDetails?.defaultCurrency);

  const nextOrderDate = useMemo(() => {
    if (!automatedPayment) return '';

    let date;
    if (frequency === automatedPayment.frequency) {
      date = dayjs(automatedPayment?.nextPaymentScheduledAt).format('DD MMM, YYYY');
    } else {
      date = dayjs(automatedPayment?.nextPaymentScheduledAt)
        .subtract(automatedPayment.frequency, 'days')
        .add(frequency, 'days')
        .format('DD MMM, YYYY');
    }
    // Adjust date if it's in the past
    while (dayjs(date).isBefore(dayjs())) {
      date = dayjs(date).add(frequency, 'days').format('DD MMM, YYYY');
    }
    return date;
  }, [frequency, automatedPayment]);

  return (
    <div
      className={clsx(
        'text-xs py-2 border-default border-dashed text-center rounded',
        automatedPayment?.status === 'paused' ? 'text-yellow' : 'text-success',
        mode === 'create' ? 'mt-4 mb-6' : 'mb-4',
        isRetry ? 'text-danger border-dashed-danger' : ''
      )}>
      {automatedPayment?.status === 'paused' ? (
        <>
          Automation Paused on {dayjs(automatedPayment?.pausedDate).format('DD MMMM, YYYY')}. Resume to send the next
          payment
        </>
      ) : mode === 'create' ? (
        <>
          {currencyLogo}
          {amount} will be sent {data} time{data > 1 ? 's' : ''} between {dayjs(currentDate).format('DD MMM, YYYY')} -{' '}
          {dayjs(endDate).format('DD MMM, YYYY')}
        </>
      ) : isRetry ? (
        <>
          Payment of ${Number(automatedPayment?.amount).toFixed(2)} failed on{' '}
          {dayjs(automatedPayment?.createdAt).format('DD MMMM, YYYY')}. Retry now.
        </>
      ) : (
        <>
          The next payment of {currencyLogo}
          {Number(automatedPayment?.amount).toFixed(2)} is scheduled for {nextOrderDate}
        </>
      )}
    </div>
  );
};

export default PaymentInfoTag;
